import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { CheckIcon, ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

export type DropdownOption = {
  value: string;
  label: string;
};

type DropdownProps = {
  label?: string;
  options: DropdownOption[];
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  emptyMessage?: string;
} & (
  | {
      multiple?: false;
      value: string;
      onChange: (value: string) => void;
    }
  | {
      multiple: true;
      value: string[];
      onChange: (value: string[]) => void;
    }
);

function selectedLabel(options: DropdownOption[], value: string, placeholder: string): string {
  return options.find((option) => option.value === value)?.label || placeholder;
}

export default function Dropdown(props: DropdownProps) {
  const {
    label,
    options,
    placeholder = 'Select',
    disabled = false,
    error,
    emptyMessage = 'No options available',
  } = props;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const multiple = props.multiple === true;
  const selectedValues = multiple ? props.value : props.value ? [props.value] : [];

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((option) => option.label.toLowerCase().includes(needle));
  }, [options, query]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      return;
    }

    function handlePointer(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    function handleKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    searchRef.current?.focus();
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  function toggleValue(next: string) {
    if (multiple) {
      const exists = selectedValues.includes(next);
      props.onChange(exists ? selectedValues.filter((value) => value !== next) : [...selectedValues, next]);
      return;
    }
    props.onChange(next);
    setOpen(false);
  }

  const triggerText = multiple
    ? selectedValues.length
      ? `${selectedValues.length} selected`
      : placeholder
    : selectedLabel(options, props.multiple ? '' : props.value, placeholder);

  const showSearch = options.length > 8;

  return (
    <div className="w-full" ref={rootRef}>
      {label && <p className="block text-sm font-medium text-gray-700 mb-2">{label}</p>}

      {multiple && selectedValues.length > 0 && (
        <ul className="flex flex-wrap gap-2 mb-2">
          {selectedValues.map((value) => {
            const option = options.find((item) => item.value === value);
            return (
              <li key={value}>
                <span className="inline-flex items-center gap-1 pl-3 pr-1 py-1 rounded-full bg-primary/10 text-primary text-sm">
                  {option?.label || value}
                  <button
                    type="button"
                    className="min-h-[32px] min-w-[32px] inline-flex items-center justify-center rounded-full hover:bg-primary/10 focus-ring"
                    aria-label={`Remove ${option?.label || value}`}
                    onClick={() => toggleValue(value)}
                    disabled={disabled}
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </span>
              </li>
            );
          })}
        </ul>
      )}

      <button
        type="button"
        disabled={disabled || options.length === 0}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((current) => !current)}
        className={`
          w-full px-4 py-3 border rounded-md bg-white text-left
          flex items-center justify-between gap-2 min-h-[48px]
          focus:border-primary focus:ring-2 focus:ring-primary focus:ring-offset-0
          disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed
          ${error ? 'border-error' : 'border-gray-300'}
          ${open ? 'border-primary ring-2 ring-primary' : ''}
        `}
      >
        <span className={selectedValues.length || (!multiple && props.value) ? 'text-gray-900' : 'text-gray-500'}>
          {options.length === 0 ? emptyMessage : triggerText}
        </span>
        <ChevronDownIcon className={`h-5 w-5 text-gray-400 flex-shrink-0 ${open ? 'rotate-180' : ''}`} aria-hidden />
      </button>

      {open && options.length > 0 && (
        <div className="relative z-20">
          <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-72 overflow-hidden">
            {showSearch && (
              <div className="p-2 border-b border-gray-200">
                <label className="relative block">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" aria-hidden />
                  <input
                    ref={searchRef}
                    type="search"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search"
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:border-primary focus:ring-2 focus:ring-primary"
                  />
                </label>
              </div>
            )}
            <ul id={listId} role="listbox" aria-multiselectable={multiple} className="overflow-y-auto max-h-60 py-1">
              {filtered.length === 0 ? (
                <li className="px-4 py-3 text-sm text-gray-500">No matches</li>
              ) : (
                filtered.map((option) => {
                  const selected = selectedValues.includes(option.value);
                  return (
                    <li key={option.value}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={selected}
                        className={`w-full min-h-[44px] px-4 py-2 text-left text-sm flex items-center justify-between gap-2 hover:bg-gray-50 focus-ring ${
                          selected ? 'text-primary font-medium' : 'text-gray-700'
                        }`}
                        onClick={() => toggleValue(option.value)}
                      >
                        {option.label}
                        {selected && <CheckIcon className="h-4 w-4 flex-shrink-0" aria-hidden />}
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
          </div>
        </div>
      )}

      {error && <p className="mt-1 text-sm text-error">{error}</p>}
    </div>
  );
}
